// src/components/shopping/ThriftProfileModal.tsx
import React, { useState, useEffect } from 'react';
import { 
  X, Award, Loader2, MessageSquare, ShoppingBag, MapPin, 
  Factory, Ruler, Clock, CheckCircle, ArrowLeft, Share2, 
  Flag, Users, Heart, Instagram, Twitter, Youtube, Music2, 
  Globe, Link as LinkIcon, ChevronRight, Package, Menu, 
  LayoutGrid, Layers, Bell, Coins, Mail, Info, Store, 
  ShieldCheck, Send, UserPlus, Flame, Ticket, Grid, 
  LayoutTemplate, ArrowRight, Lock, Play, Image as ImageIcon,
  FileText, Tag, Sparkles, Shield, Truck, Facebook, Eye, Calendar,
  Star, StarHalf, ThumbsUp, Zap, TrendingUp
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../services/firebase';
import { doc, setDoc, serverTimestamp, collection, addDoc, getDoc, getDocs, query, where, updateDoc, increment } from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth';

// ============================================================================
// FULL PAGE THRIFT PROFILE
// ============================================================================
export const ThriftProfilePage = ({ 
  brand: initialBrand, 
  products: initialProducts = [], 
  onClose,
  onSelectProduct,
  onSendMessage
}: any) => {
  const navigate = useNavigate();
  const { thriftId: paramThriftId } = useParams<{ thriftId: string }>();
  const { user: currentUser, profile: userProfile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [activeTab, setActiveTab] = useState<'shop' | 'about'>('shop');
  const [brand, setBrand] = useState<any>(initialBrand || null);
  const [products, setProducts] = useState<any[]>(initialProducts || []);
  const [loading, setLoading] = useState(!initialBrand);
  const [soldCount, setSoldCount] = useState(0);
  const [lastActive, setLastActive] = useState<string | null>(null);

  // 🟢 PRODUCT CLICK HANDLER
  const handleProductClick = (product: any) => {
    console.log('🔍 Product clicked in thrift profile:', product);
    if (onSelectProduct) {
      onSelectProduct(product);
    } else {
      console.warn('⚠️ onSelectProduct not provided - product click will not work');
    }
  };

  // 🟢 MESSAGE HANDLER
  const handleSendMessage = () => {
    console.log('🔍 Message Store clicked');
    
    if (!currentUser) {
      window.dispatchEvent(new CustomEvent('open-login-modal'));
      return;
    }
    
    const sellerId = brand?.id || brand?.uid || brand?.workspaceId;
    console.log('🔍 Seller ID:', sellerId);
    
    if (!sellerId) {
      alert("Unable to message this store.");
      return;
    }
    
    setIsSubmitting(true);
    
    if (onSendMessage) {
      onSendMessage(sellerId, brand?.businessName || brand?.displayName || 'Thrift Store');
      setIsSubmitting(false);
      return;
    }
    
    try {
      window.dispatchEvent(new CustomEvent('open-messages', {
        detail: { userId: sellerId }
      }));
    } catch (e) {
      console.error(e);
      alert("Failed to open chat.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // Fetch Data
  // ============================================================================
  useEffect(() => {
    const fetchThriftData = async () => {
      const thriftId = initialBrand?.id || initialBrand?.uid || paramThriftId;
      
      if (!thriftId) {
        console.log('No thrift ID found');
        return;
      }

      try {
        setLoading(true);
        
        let brandData = initialBrand;
        if (!brandData) {
          const userDoc = await getDoc(doc(db, 'users', thriftId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            try {
              await updateDoc(doc(db, 'users', thriftId), {
                lastActive: serverTimestamp()
              });
            } catch (e) {
              console.error('Error updating last active:', e);
            }
            
            let sold = 0;
            try {
              const productQuery = query(
                collection(db, 'products'),
                where('workspaceId', '==', thriftId),
                where('status', '==', 'sold')
              );
              const productSnap = await getDocs(productQuery);
              sold = productSnap.size;
            } catch (e) {
              console.error('Error fetching sold count:', e);
            }
            setSoldCount(sold);
            
            if (userData.lastActive) {
              const lastActiveDate = userData.lastActive.toDate ? userData.lastActive.toDate() : new Date(userData.lastActive);
              const now = new Date();
              const diff = Math.floor((now.getTime() - lastActiveDate.getTime()) / 1000);
              if (diff < 60) {
                setLastActive('Just now');
              } else if (diff < 3600) {
                setLastActive(`${Math.floor(diff / 60)}m ago`);
              } else if (diff < 86400) {
                setLastActive(`${Math.floor(diff / 3600)}h ago`);
              } else if (diff < 604800) {
                setLastActive(`${Math.floor(diff / 86400)}d ago`);
              } else {
                setLastActive(lastActiveDate.toLocaleDateString());
              }
            } else {
              setLastActive('Recently');
            }
            
            brandData = {
              id: userDoc.id,
              uid: userDoc.id,
              ...userData,
              businessName: userData.businessName || userData.displayName || userData.username || 'Thrift Store',
              displayName: userData.displayName || userData.businessName || userData.username || 'Thrift Store',
              location: userData.location || userData.sellerLocation || 'Global',
              photoURL: userData.photoURL || userData.logoUrl || null,
              logoUrl: userData.logoUrl || userData.photoURL || null,
              bannerUrl: userData.bannerUrl || null,
              bio: userData.description || userData.bio || '',
              description: userData.description || userData.bio || '',
              theme: userData.theme || userData.storefront?.theme || 'vintage',
              storefront: userData.storefront || {},
              verified: userData.verified || false,
              followersCount: userData.followersCount || userData.followers || 0,
              showAuthenticationBadge: userData.showAuthenticationBadge !== undefined ? userData.showAuthenticationBadge : true,
              showConditionGrades: userData.showConditionGrades !== undefined ? userData.showConditionGrades : true,
              featuredEra: userData.featuredEra || '90s',
              curatorsNote: userData.curatorsNote || '',
              layout: userData.layout || 'grid',
              primaryColor: userData.primaryColor || '#D97706',
              province: userData.province || 'Gauteng',
              instagramHandle: userData.instagramHandle || '',
              tiktokHandle: userData.tiktokHandle || '',
              twitterHandle: userData.twitterHandle || '',
              facebookHandle: userData.facebookHandle || '',
              youtubeHandle: userData.youtubeHandle || '',
              footerText: userData.footerText || '',
              footerSocialLinks: userData.footerSocialLinks || { 
                instagram: '', tiktok: '', twitter: '', facebook: '', youtube: '' 
              },
              showStoreName: userData.showStoreName !== undefined ? userData.showStoreName : true,
              showFollowButton: userData.showFollowButton !== undefined ? userData.showFollowButton : true,
              showContactButton: userData.showContactButton !== undefined ? userData.showContactButton : true,
              showShareButton: userData.showShareButton !== undefined ? userData.showShareButton : true,
              showReportButton: userData.showReportButton !== undefined ? userData.showReportButton : true,
            };
            setBrand(brandData);
          } else {
            console.log('No user found for ID:', thriftId);
            return;
          }
        } else {
          setBrand(brandData);
        }

        if (brandData) {
          const thriftIdToUse = brandData.id || brandData.uid || thriftId;
          
          let productsQuery = query(
            collection(db, 'products'),
            where('workspaceId', '==', thriftIdToUse)
          );
          let productsSnap = await getDocs(productsQuery);
          
          if (productsSnap.empty) {
            productsQuery = query(
              collection(db, 'products'),
              where('sellerId', '==', thriftIdToUse)
            );
            productsSnap = await getDocs(productsQuery);
          }
          
          if (productsSnap.empty) {
            productsQuery = query(
              collection(db, 'products'),
              where('businessId', '==', thriftIdToUse)
            );
            productsSnap = await getDocs(productsQuery);
          }

          if (productsSnap.empty) {
            productsQuery = query(
              collection(db, 'products'),
              where('workspaceType', '==', 'thrift')
            );
            productsSnap = await getDocs(productsQuery);
            
            const allProducts = productsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            const filteredProducts = allProducts.filter((p: any) => 
              p.sellerId === thriftIdToUse || 
              p.workspaceId === thriftIdToUse || 
              p.businessId === thriftIdToUse ||
              p.sellerId === brandData.id ||
              p.workspaceId === brandData.id
            );
            setProducts(filteredProducts);
            
            const soldItems = filteredProducts.filter((p: any) => p.status === 'sold' || p.stock === 0);
            setSoldCount(soldItems.length);
            return;
          }

          const productList = productsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          setProducts(productList);
          
          const soldItems = productList.filter((p: any) => p.status === 'sold' || p.stock === 0);
          setSoldCount(soldItems.length);
        }
      } catch (error) {
        console.error('Error fetching thrift data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchThriftData();
  }, [initialBrand, paramThriftId]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}/thrift/${brand?.id || brand?.uid || ''}`;
    navigator.clipboard.writeText(shareUrl);
    setShowShareMenu(false);
    alert("Link copied to clipboard!");
  };

  const handleNativeShare = async () => {
    const shareUrl = `${window.location.origin}/thrift/${brand?.id || brand?.uid || ''}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: brand?.businessName || 'Thrift Store',
          text: `Check out ${brand?.businessName || 'Thrift Store'} on Scoop Hype! 🔥`,
          url: shareUrl
        });
        setShowShareMenu(false);
      } catch (err) {
        console.error("Share failed:", err);
      }
    } else {
      handleCopyLink();
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen w-full flex items-center justify-center ${brand?.theme === 'dark' || brand?.theme === 'warehouse' ? 'bg-black' : 'bg-white'}`}>
        <Loader2 className={`animate-spin ${brand?.theme === 'dark' || brand?.theme === 'warehouse' ? 'text-white' : 'text-black'}`} size={40} />
      </div>
    );
  }

  if (!brand) return null;

  const brandId = brand.uid || brand.id || brand.workspaceId || brand.businessId;
  
  const brandName = brand.businessName || 
                   brand.displayName || 
                   brand.rootName || 
                   brand.name || 
                   'Thrift Store';
  
  const brandImage = brand.photoURL || 
                    brand.logoUrl || 
                    brand.logo || 
                    brand.avatar || 
                    'https://placehold.co/150x150/eee/999?text=Thrift';
  
  const bannerImage = brand.bannerUrl || '';
  
  const location = brand.location || 
                  brand.sellerLocation || 
                  brand.userLocation || 
                  brand.address?.city || 
                  'Global';
  
  const province = brand.province || 'Gauteng';
  const fullLocation = location + (province ? `, ${province}` : '');
  
  const bio = brand.description || brand.bio || '';
  
  const followersCount = brand.followersCount || brand.followers || 0;

  const theme = brand.theme || 'vintage';
  const isDark = theme === 'dark' || theme === 'warehouse';
  
  const showAuthenticationBadge = brand.showAuthenticationBadge !== undefined ? brand.showAuthenticationBadge : true;
  const showConditionGrades = brand.showConditionGrades !== undefined ? brand.showConditionGrades : true;
  const featuredEra = brand.featuredEra || '90s';
  const curatorsNote = brand.curatorsNote || '';
  const layout = brand.layout || 'grid';

  const showStoreName = brand.showStoreName !== undefined ? brand.showStoreName : true;

  const socialLinks = {
    instagram: brand.instagramHandle || '',
    tiktok: brand.tiktokHandle || '',
    twitter: brand.twitterHandle || '',
    facebook: brand.facebookHandle || '',
    youtube: brand.youtubeHandle || '',
  };

  const footerText = brand.footerText || '';

  const getThemeStyles = () => {
    switch(theme) {
      case 'vintage':
        return {
          bg: 'bg-amber-50',
          text: 'text-amber-900',
          subtext: 'text-amber-700/70',
          border: 'border-amber-200',
          card: 'bg-white',
          accent: 'bg-amber-600',
          accentText: 'text-amber-600',
          cardHover: 'hover:shadow-lg hover:border-amber-400',
        };
      case 'dark':
        return {
          bg: 'bg-zinc-900',
          text: 'text-zinc-100',
          subtext: 'text-zinc-400',
          border: 'border-zinc-700',
          card: 'bg-zinc-800',
          accent: 'bg-amber-500',
          accentText: 'text-amber-500',
          cardHover: 'hover:shadow-lg hover:border-amber-500/50',
        };
      case 'minimal':
        return {
          bg: 'bg-white',
          text: 'text-black',
          subtext: 'text-gray-500',
          border: 'border-gray-200',
          card: 'bg-gray-50',
          accent: 'bg-amber-600',
          accentText: 'text-amber-600',
          cardHover: 'hover:shadow-lg hover:border-amber-400',
        };
      case 'warehouse':
        return {
          bg: 'bg-stone-100',
          text: 'text-stone-800',
          subtext: 'text-stone-600',
          border: 'border-stone-300',
          card: 'bg-white',
          accent: 'bg-amber-700',
          accentText: 'text-amber-700',
          cardHover: 'hover:shadow-lg hover:border-amber-500',
        };
      default:
        return {
          bg: 'bg-white',
          text: 'text-black',
          subtext: 'text-gray-500',
          border: 'border-gray-200',
          card: 'bg-gray-50',
          accent: 'bg-amber-600',
          accentText: 'text-amber-600',
          cardHover: 'hover:shadow-lg hover:border-amber-400',
        };
    }
  };

  const styles = getThemeStyles();

  return (
    <div className={`min-h-screen w-full flex flex-col ${styles.bg} ${styles.text}`}>
      
      {/* Navigation */}
      <div className={`sticky top-0 z-10 px-4 sm:px-6 md:px-8 py-3 sm:py-4 flex justify-between items-center border-b ${styles.border} ${theme === 'dark' ? 'bg-zinc-900/95' : 'bg-white/95'} backdrop-blur-sm`}>
        <button 
          onClick={handleClose} 
          className={`flex items-center gap-2 transition-colors text-sm font-bold ${styles.subtext} hover:${styles.text}`}
        >
          <ArrowLeft size={18} />
          <span className="hidden sm:inline uppercase tracking-widest">Back</span>
        </button>
        
        <h2 className={`text-xs sm:text-sm font-black uppercase tracking-widest truncate max-w-[150px] sm:max-w-xs ${styles.subtext}`}>
          {brandName}
        </h2>
        
        <button 
          onClick={() => setShowShareMenu(true)}
          className={`p-2 rounded-full transition-colors ${theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
        >
          <Share2 size={18} />
        </button>
      </div>

      {/* Profile Content */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 md:px-8 py-4 sm:py-6">
        <div className="max-w-5xl mx-auto">
          
          {/* Banner */}
          {bannerImage && (
            <div className="w-full h-48 sm:h-64 rounded-2xl overflow-hidden mb-6 shadow-xl">
              <img src={bannerImage} className="w-full h-full object-cover" alt="Banner" />
            </div>
          )}

          {/* Logo + Name Section */}
          <div className="flex flex-col items-center text-center">
            <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 ${theme === 'dark' ? 'border-zinc-700' : 'border-amber-200'} bg-white/10 overflow-hidden shadow-2xl flex items-center justify-center mb-3`}>
              {brandImage ? (
                <img src={brandImage} className="w-full h-full object-cover" alt={brandName} />
              ) : (
                <span className={`text-3xl sm:text-4xl font-black ${styles.subtext} uppercase`}>{brandName.charAt(0)}</span>
              )}
            </div>
            
            {showStoreName && (
              <h1 className={`text-2xl sm:text-3xl md:text-4xl font-black uppercase tracking-tight ${styles.text}`}>
                {brandName}
              </h1>
            )}
            
            <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
              <div className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-widest ${styles.subtext}`}>
                <MapPin size={12} /> {fullLocation}
              </div>
              {showAuthenticationBadge && (
                <Shield size={14} className="text-amber-500" fill="currentColor" />
              )}
              {brand.verified && (
                <CheckCircle size={14} className="text-orange-500" fill="currentColor" />
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 mt-4 max-w-sm mx-auto">
              <div className={`p-2.5 rounded-xl border ${theme === 'dark' ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                <span className={`text-[8px] font-black uppercase block text-center ${styles.subtext}`}>
                  <Package size={12} className="inline mr-1" /> Items
                </span>
                <p className={`font-black text-lg text-center ${styles.text}`}>
                  {products.length}
                </p>
              </div>
              <div className={`p-2.5 rounded-xl border ${theme === 'dark' ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                <span className={`text-[8px] font-black uppercase block text-center ${styles.subtext}`}>
                  <ShoppingBag size={12} className="inline mr-1" /> Sold
                </span>
                <p className={`font-black text-lg text-center ${styles.text}`}>
                  {soldCount}
                </p>
              </div>
              <div className={`p-2.5 rounded-xl border ${theme === 'dark' ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                <span className={`text-[8px] font-black uppercase block text-center ${styles.subtext}`}>
                  <Clock size={12} className="inline mr-1" /> Active
                </span>
                <p className={`font-black text-xs text-center ${styles.text}`}>
                  {lastActive || 'Recently'}
                </p>
              </div>
            </div>

            {/* Store Badges */}
            <div className="flex flex-wrap justify-center gap-2 mt-3">
              <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase ${theme === 'dark' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-amber-100 text-amber-700 border border-amber-200'}`}>
                <Tag size={10} className="inline mr-1" /> {featuredEra}
              </span>
              {showConditionGrades && (
                <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase ${theme === 'dark' ? 'bg-white/10 text-white border border-white/10' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}>
                  9/10+ Graded
                </span>
              )}
              {showAuthenticationBadge && (
                <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase ${theme === 'dark' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-green-100 text-green-700 border border-green-200'}`}>
                  <Shield size={10} className="inline mr-1" /> Authenticated
                </span>
              )}
            </div>

            {/* Curator's Note */}
            {curatorsNote && (
              <div className={`mt-3 p-4 rounded-xl ${theme === 'dark' ? 'bg-zinc-800/50 border border-zinc-700' : 'bg-amber-50/50 border border-amber-200'} max-w-xl mx-auto text-sm italic ${styles.subtext}`}>
                "{curatorsNote}"
              </div>
            )}

            {/* Bio */}
            {bio && (
              <p className={`text-sm mt-3 max-w-xl mx-auto ${styles.subtext}`}>
                {bio}
              </p>
            )}

            {/* Social Icons */}
            <div className="flex flex-wrap justify-center gap-2 mt-3">
              {socialLinks.instagram && (
                <a href={`https://instagram.com/${socialLinks.instagram}`} target="_blank" rel="noreferrer" className={`p-2 border rounded-lg transition-colors ${theme === 'dark' ? 'border-white/10 hover:bg-white/10 text-white/60 hover:text-white' : 'border-gray-200 hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`}>
                  <Instagram size={16} />
                </a>
              )}
              {socialLinks.tiktok && (
                <a href={`https://tiktok.com/@${socialLinks.tiktok}`} target="_blank" rel="noreferrer" className={`p-2 border rounded-lg transition-colors ${theme === 'dark' ? 'border-white/10 hover:bg-white/10 text-white/60 hover:text-white' : 'border-gray-200 hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`}>
                  <Music2 size={16} />
                </a>
              )}
              {socialLinks.twitter && (
                <a href={`https://twitter.com/${socialLinks.twitter}`} target="_blank" rel="noreferrer" className={`p-2 border rounded-lg transition-colors ${theme === 'dark' ? 'border-white/10 hover:bg-white/10 text-white/60 hover:text-white' : 'border-gray-200 hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`}>
                  <Twitter size={16} />
                </a>
              )}
              {socialLinks.facebook && (
                <a href={socialLinks.facebook} target="_blank" rel="noreferrer" className={`p-2 border rounded-lg transition-colors ${theme === 'dark' ? 'border-white/10 hover:bg-white/10 text-white/60 hover:text-white' : 'border-gray-200 hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`}>
                  <Facebook size={16} />
                </a>
              )}
              {socialLinks.youtube && (
                <a href={socialLinks.youtube} target="_blank" rel="noreferrer" className={`p-2 border rounded-lg transition-colors ${theme === 'dark' ? 'border-white/10 hover:bg-white/10 text-white/60 hover:text-white' : 'border-gray-200 hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`}>
                  <Youtube size={16} />
                </a>
              )}
            </div>

            {/* 🟢 Message Store Button */}
            <div className="flex gap-3 w-full max-w-md mt-4">
              <button 
                onClick={() => {
                  console.log('🔍 Message Store button clicked');
                  handleSendMessage();
                }}
                disabled={isSubmitting}
                className={`flex-1 py-3 sm:py-3.5 font-black uppercase text-[10px] sm:text-xs tracking-widest rounded-xl transition-all flex justify-center items-center gap-2 ${
                  theme === 'dark' 
                    ? 'border border-white/20 text-white hover:bg-white/10' 
                    : 'border border-gray-300 text-gray-900 hover:bg-gray-100'
                } disabled:opacity-50`}
              >
                {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />} 
                <span>Message Store</span>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className={`flex justify-center border-b mt-6 sm:mt-8 ${styles.border}`}>
            {['shop', 'about'].map(tab => (
              <button 
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`px-6 sm:px-10 py-3 sm:py-4 text-[11px] sm:text-xs font-black uppercase tracking-[0.2em] transition-colors border-b-2 ${
                  activeTab === tab 
                    ? `border-amber-500 ${styles.text}`
                    : `border-transparent ${styles.subtext} hover:${styles.text}`
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="mt-4 sm:mt-6">
            {activeTab === 'shop' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-[10px] sm:text-[11px] font-black uppercase tracking-widest ${styles.subtext}`}>
                    <Package size={12} className="inline mr-1" /> Listings ({products.length})
                  </h3>
                  {showConditionGrades && (
                    <span className={`text-[9px] font-black uppercase ${styles.subtext}`}>
                      Graded • Authenticated
                    </span>
                  )}
                </div>
                
                {products.length === 0 ? (
                  <div className={`text-center py-16 rounded-2xl border border-dashed ${theme === 'dark' ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                    <Package className={`mx-auto mb-3 ${theme === 'dark' ? 'text-zinc-600' : 'text-gray-300'}`} size={32} />
                    <p className={`text-xs font-bold uppercase tracking-widest ${styles.subtext}`}>
                      No items listed yet.
                    </p>
                  </div>
                ) : (
                  <div className={`grid ${layout === 'grid' ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5' : 'grid-cols-1'} gap-3 sm:gap-4`}>
                    {products.map((p: any) => {
                      const isSoldOut = p.status === 'sold' || p.stock === 0;
                      const condition = p.condition || '8/10';
                      const era = p.era || featuredEra;
                      
                      return (
                        <div 
                          key={p.id} 
                          onClick={() => {
                            console.log('🔍 Product card clicked:', p.id);
                            handleProductClick(p);
                          }}
                          className={`group cursor-pointer p-3 rounded-xl border transition-all duration-300 ${
                            theme === 'dark' 
                              ? 'border-white/10 hover:border-amber-500/50 bg-white/5 hover:bg-white/10' 
                              : 'border-gray-200 hover:border-amber-400 bg-white hover:shadow-xl'
                          } ${isSoldOut ? 'opacity-60 hover:opacity-80' : ''} ${styles.cardHover}`}
                        >
                          <div className="aspect-square bg-zinc-800 rounded-lg mb-2 overflow-hidden relative">
                            {p.image || p.images?.[0] ? (
                              <img 
                                src={p.image || p.images?.[0]} 
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                                alt={p.name} 
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package size={24} className={theme === 'dark' ? 'text-zinc-600' : 'text-gray-300'} />
                              </div>
                            )}
                            {isSoldOut && (
                              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                <span className="text-white font-black text-xs uppercase tracking-widest border-2 border-white px-3 py-1 rotate-[-15deg]">SOLD</span>
                              </div>
                            )}
                            {showConditionGrades && !isSoldOut && (
                              <div className="absolute top-1.5 left-1.5 px-2 py-0.5 bg-black/80 rounded-lg text-white text-[7px] font-black uppercase tracking-wider backdrop-blur-sm">
                                {condition}
                              </div>
                            )}
                            {showAuthenticationBadge && !isSoldOut && (
                              <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 bg-green-500/90 rounded-lg text-white text-[7px] font-black uppercase tracking-wider backdrop-blur-sm">
                                ✓
                              </div>
                            )}
                          </div>
                          <h4 className={`font-black text-[11px] sm:text-xs truncate ${styles.text}`}>
                            {p.name}
                          </h4>
                          <p className={`text-[8px] sm:text-[9px] font-bold uppercase truncate ${styles.subtext}`}>
                            {p.category || 'Vintage'} • {era}
                          </p>
                          <div className="flex items-center justify-between mt-1.5">
                            <p className="text-sm font-black text-amber-500">
                              R{p.price}
                            </p>
                            {p.brandName && (
                              <span className={`text-[7px] sm:text-[8px] font-bold ${styles.subtext}`}>
                                {p.brandName}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'about' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`p-5 rounded-2xl border ${theme === 'dark' ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                  <h3 className={`text-[10px] font-black uppercase tracking-widest mb-2 flex items-center gap-2 ${styles.subtext}`}>
                    <Info size={14} /> About
                  </h3>
                  <p className={`text-sm leading-relaxed ${styles.subtext}`}>
                    {bio || 'No description provided.'}
                  </p>
                </div>

                <div className={`p-5 rounded-2xl border ${theme === 'dark' ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                  <h3 className={`text-[10px] font-black uppercase tracking-widest mb-2 flex items-center gap-2 ${styles.subtext}`}>
                    <Store size={14} /> Location
                  </h3>
                  <div className={`flex items-center gap-2 text-sm ${styles.subtext}`}>
                    <MapPin size={16} /> <span>{fullLocation}</span>
                  </div>
                </div>

                <div className={`p-5 rounded-2xl border ${theme === 'dark' ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                  <h3 className={`text-[10px] font-black uppercase tracking-widest mb-2 flex items-center gap-2 ${styles.subtext}`}>
                    <Tag size={14} /> Era Focus
                  </h3>
                  <p className={`text-sm ${styles.subtext}`}>
                    Featured: <span className={`font-bold ${styles.text}`}>{featuredEra}</span>
                  </p>
                  {showConditionGrades && (
                    <p className={`text-sm mt-1 ${styles.subtext}`}>
                      Grading: <span className={`font-bold ${styles.text}`}>10/10 to 7/10</span>
                    </p>
                  )}
                </div>

                <div className={`p-5 rounded-2xl border ${theme === 'dark' ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                  <h3 className={`text-[10px] font-black uppercase tracking-widest mb-2 flex items-center gap-2 ${styles.subtext}`}>
                    <Shield size={14} /> Authentication
                  </h3>
                  <p className={`text-sm ${styles.subtext}`}>
                    {showAuthenticationBadge ? (
                      <span className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-green-500" />
                        Verified Thrift Store
                      </span>
                    ) : (
                      'Standard Store'
                    )}
                  </p>
                </div>

                {curatorsNote && (
                  <div className={`p-5 rounded-2xl border col-span-1 md:col-span-2 ${theme === 'dark' ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                    <h3 className={`text-[10px] font-black uppercase tracking-widest mb-2 flex items-center gap-2 ${styles.subtext}`}>
                      <Sparkles size={14} /> Curator's Note
                    </h3>
                    <p className={`text-sm italic ${styles.subtext}`}>
                      "{curatorsNote}"
                    </p>
                  </div>
                )}

                {footerText && (
                  <div className={`p-5 rounded-2xl border col-span-1 md:col-span-2 ${theme === 'dark' ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                    <p className={`text-xs text-center ${styles.subtext}`}>
                      {footerText}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Share Menu Modal */}
      {showShareMenu && (
        <div className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-4 sm:p-6" onClick={() => setShowShareMenu(false)}>
          <div className="bg-zinc-900 border border-white/10 rounded-[2rem] p-6 sm:p-8 w-full max-w-sm text-center shadow-2xl animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Share2 className="text-white" size={28} />
            </div>
            <h3 className="text-white font-black uppercase italic tracking-widest text-lg sm:text-xl mb-2">Share Store</h3>
            <p className="text-zinc-500 text-xs mb-6 sm:mb-8 uppercase font-bold tracking-tighter">Share this thrift store.</p>
            <div className="space-y-3">
              <button onClick={handleNativeShare} className="w-full py-3 sm:py-4 bg-white text-black rounded-xl font-black uppercase text-xs tracking-widest hover:bg-zinc-200 transition-all active:scale-95 flex items-center justify-center gap-2">
                <Users size={16} /> Share
              </button>
              <button onClick={handleCopyLink} className="w-full py-3 sm:py-4 bg-zinc-800 text-white rounded-xl font-black uppercase text-xs tracking-widest hover:bg-zinc-700 transition-all active:scale-95 flex items-center justify-center gap-2">
                <LinkIcon size={16} /> Copy Link
              </button>
            </div>
            <button onClick={() => setShowShareMenu(false)} className="mt-6 text-zinc-600 uppercase font-black text-[10px] hover:text-white transition-colors tracking-[0.2em]">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MODAL WRAPPER - FULL SCREEN
// ============================================================================
export const ThriftProfileModal = ({ 
  isOpen, 
  onClose, 
  brand, 
  products = [], 
  onSelectProduct,
  onSendMessage 
}: any) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[50] w-full h-full">  {/* ✅ Changed from z-[900] to z-[50] */}
      <ThriftProfilePage 
        brand={brand} 
        products={products} 
        onClose={onClose}
        onSelectProduct={onSelectProduct}
        onSendMessage={onSendMessage}
      />
    </div>
  );
};

export default ThriftProfileModal;